import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ExclamationTriangleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

export interface ConfirmOptions {
  /** Mensagem principal. Obrigatória. */
  mensagem: string;
  /** Título do dialog. Default "Confirmar". */
  titulo?: string;
  /** Texto do botão de confirmação. Default "Confirmar" (ou "Excluir" se destrutivo). */
  textoConfirmar?: string;
  /** Texto do botão cancelar. Default "Cancelar". */
  textoCancelar?: string;
  /** Marca como destrutiva — botão fica em rose, ícone amber de alerta. Default false. */
  destrutivo?: boolean;
}

interface ConfirmDialogContextValue {
  /**
   * Abre o dialog e retorna `Promise<boolean>`. Resolve `true` se o usuário
   * confirma, `false` se cancela ou fecha (Esc, clique fora).
   *
   * Uso:
   *   const confirm = useConfirmDialog();
   *   const ok = await confirm({ mensagem: 'Excluir?', destrutivo: true });
   *   if (!ok) return;
   *   ...
   */
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextValue | null>(null);

interface DialogState extends ConfirmOptions {
  resolve: (v: boolean) => void;
}

/**
 * Provider do ConfirmDialog. Substitui `window.confirm()` nativo. Acessível
 * (role=dialog, aria-modal, fecha com Esc, foco inicial no botão de cancelar
 * pra evitar confirmação acidental).
 */
export const ConfirmDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<DialogState | null>(null);
  const cancelarRef = useRef<HTMLButtonElement>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setState({ ...opts, resolve });
    });
  }, []);

  const fechar = useCallback((valor: boolean) => {
    if (!state) return;
    state.resolve(valor);
    setState(null);
  }, [state]);

  // Foco inicial no botão Cancelar — evita confirmação acidental por Enter.
  useEffect(() => {
    if (state) cancelarRef.current?.focus();
  }, [state]);

  // Fecha com Esc.
  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fechar(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, fechar]);

  return (
    <ConfirmDialogContext.Provider value={{ confirm }}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) fechar(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-titulo"
            aria-describedby="confirm-mensagem"
            className="w-full max-w-sm rounded-lg border border-zinc-200 bg-white shadow-xl dark:border-zinc-800 dark:bg-zinc-900"
          >
            <div className="flex items-start gap-3 border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
              {state.destrutivo ? (
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
              ) : (
                <QuestionMarkCircleIcon className="h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden />
              )}
              <h2 id="confirm-titulo" className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                {state.titulo ?? 'Confirmar'}
              </h2>
            </div>
            <div className="p-4">
              <p id="confirm-mensagem" className="text-sm text-zinc-700 dark:text-zinc-300">
                {state.mensagem}
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-zinc-200 px-4 py-3 dark:border-zinc-800">
              <button
                ref={cancelarRef}
                type="button"
                onClick={() => fechar(false)}
                className="h-9 rounded-md border border-zinc-200 bg-white px-4 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
              >
                {state.textoCancelar ?? 'Cancelar'}
              </button>
              <button
                type="button"
                onClick={() => fechar(true)}
                className={`h-9 rounded-md px-4 text-sm font-medium text-white ${
                  state.destrutivo
                    ? 'bg-rose-600 hover:bg-rose-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {state.textoConfirmar ?? (state.destrutivo ? 'Excluir' : 'Confirmar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmDialogContext.Provider>
  );
};

/**
 * Hook pra abrir dialogs de confirmação. Retorna Promise<boolean>.
 *
 *   const confirm = useConfirmDialog();
 *   if (!await confirm({ mensagem: 'Excluir?', destrutivo: true })) return;
 */
export function useConfirmDialog(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) throw new Error('useConfirmDialog precisa estar dentro de <ConfirmDialogProvider>');
  return ctx.confirm;
}

export default ConfirmDialogProvider;
